import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ConsultCTA } from './consult-cta';

export const UniversityCard = React.memo(({
  university, // Full university object from backend
  logo: logoProp, name: nameProp, location: locationProp, institutionType: typeProp, ranking: rankingProp, programsCount,
  fieldTags: tagsProp = [], verified: verifiedProp = false, hasScholarships: scholarshipsProp = false, isPartner: partnerProp = false, 
  ctaText = "Explore More", onConsultClick,
}) => {
  const navigate = useNavigate();
  
  // Extract from university object if provided, fallback to direct props
  const logo = university?.logo || university?.images?.[0] || logoProp || null;
  const name = university?.display_name || university?.name || nameProp || 'University';
  const location = university ? `${university.city}, ${university.country}` : locationProp;
  const institutionType = university?.institution_type || typeProp;
  const ranking = university?.qs_ranking ? `QS #${university.qs_ranking}` : (university?.setara_rating ? `SETARA ${university.setara_rating}★` : rankingProp);
  const fieldTags = university?.fields_of_study || tagsProp || [];
  const verified = university?.featured ?? verifiedProp;
  const hasScholarships = university?.scholarships_available ?? scholarshipsProp;
  const isPartner = (university?.partner_status === 'Close' || university?.partner_status === 'Normal') ?? partnerProp;
  
  const visibleTags = fieldTags.slice(0, 3);
  const hiddenTagsCount = Math.max(0, fieldTags.length - 3);
  
  const handleClick = () => {
    if (onConsultClick) {
      onConsultClick();
    } else if (university?.slug) {
      navigate(`/universities/${university.slug}`);
    } else if (university?.id) {
      // Fallback to ID if slug not available (backward compatibility)
      navigate(`/universities/${university.id}`);
    }
  };

  return (
    <article data-testid="university-card" className="group bg-white border border-[#E8E4DF] rounded-xl hover:border-[#D4A853] hover:shadow-lg transition-all duration-200 overflow-hidden flex flex-col h-full">
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start gap-4 mb-4">
          {/* University Logo */}
          <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-[#F8F6F3] border border-[#E8E4DF] flex items-center justify-center p-2">
            {logo ? (
              <img src={logo} alt={`${name} logo`} className="max-w-full max-h-full object-contain" loading="lazy" width="56" height="56" />
            ) : (
              <svg className="w-8 h-8 text-[#8B9AAB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[#00224F] line-clamp-2 mb-1 group-hover:text-[#D4A853] transition-colors" style={{ fontSize: '1.0rem', fontWeight: 750, lineHeight: '1.3' }}>{name}</h3>
            {location && (
              <p className="text-xs text-[#5A6978] flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                <span className="truncate">{location}</span>
              </p>
            )}
          </div>
        </div>

        {/* Institution Type */}
        {institutionType && (
          <div className="mb-3">
            <p className="text-xs text-[#5A6978]">
              <span className="font-medium">Type:</span> {institutionType}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 mb-3">
          {verified && <span className="text-xs px-2 py-0.5 rounded bg-[#E8F4F4] text-[#3D7A7A] font-medium">Verified</span>}
          {hasScholarships && <span className="text-xs px-2 py-0.5 rounded bg-[#FDF6E9] text-[#8B6914] font-medium">Scholarships</span>}
          {isPartner && <span className="text-xs px-2 py-0.5 rounded bg-[#EEF2F7] text-[#00224F] font-medium">Partner</span>}
        </div>

        {visibleTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {visibleTags.map((tag, index) => (
              <span key={index} className="text-xs px-2 py-0.5 rounded bg-[#F5F3F0] text-[#5A6978]">{tag}</span>
            ))}
            {hiddenTagsCount > 0 && <span className="text-xs px-2 py-0.5 rounded bg-[#F5F3F0] text-[#5A6978]">+{hiddenTagsCount}</span>}
          </div>
        )}

        <div className="flex items-center gap-6 mb-4 text-sm flex-grow">
          {ranking && (
            <div className="flex items-center gap-1.5">
              <span className="text-[#D4A853]">★</span>
              <span className="font-semibold text-[#00224F]">{ranking}</span>
            </div>
          )}
          {programsCount && (
            <div className="text-[#5A6978]">
              <span className="font-semibold text-[#00224F]">{programsCount}</span> programs
            </div>
          )}
        </div>

        <div className="mt-auto">
          <button 
            onClick={handleClick} 
            className="w-full px-4 py-2.5 text-sm font-semibold text-[#1E40AF] bg-[#EFF6FF] hover:bg-[#DBEAFE] rounded-lg transition-colors flex items-center justify-center gap-1"
          >
            {ctaText}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </article>
  );
});
